import { Base } from "@tt-app/api/base";
import { API_ENDPOINTS } from "@tt-app/constants/api";
import {
  PayloadAuthenticated,
  PayloadPwdChngRequest,
  PayloadPwdChngReset,
  RespPwdChngReset,
  ErrPwdChngReset
} from "@tt-app/models";
import { AxiosResponse, AxiosError } from "axios";
import { buildAuthHeaders } from "@tt-app/utils/internal/api";
import { Profile } from "@tt-app/types";

const {
  USER: {
    ALIASES,
    COURSE_HISTORY,
    GROUPS,
    INDIVIDUAL,
    ORGANIZATION,
    PROFILE,
    PWD_CHNG_REQUEST,
    PWD_CHNG_RESET,
    SET_PASSWORD_WITH_TMP_PASSWORD,
    SET_PASSWORD_WITH_JWT,
    TAG_ALIAS,
    VALIDATE_TMP_PASSWORD,
    VALIDATE_JWT,
    VALIDATE_RESET_PASSWORD_JWT
  }
} = API_ENDPOINTS;

class User extends Base {
  async authWithTmpPassword(payload: any) {
    return this.httpClient.post(VALIDATE_TMP_PASSWORD, payload);
  }

  async courseHistory(payload: any) {
    const headers = this.buildAuthHeaders(payload.authInfo);
    return this.httpClient.get(`${COURSE_HISTORY}/${payload.courseUuid}/`, { headers });
  }

  async groups(payload: any) {
    const headers = this.buildAuthHeaders(payload.authInfo);
    return this.httpClient.get(GROUPS, { headers });
  }

  async individualLogin(payload: any) {
    return this.httpClient.post(INDIVIDUAL, payload);
  }

  async organizationLogin(payload: any) {
    return this.httpClient.post(ORGANIZATION, payload);
  }

  async getProfile(payload: PayloadAuthenticated): Promise<Profile> {
    const resp: AxiosResponse<Profile> = await this.httpClient.get(PROFILE, {
      headers: buildAuthHeaders(payload)
    });
    return resp.data;
  }

  async pwdChngRequest(payload: PayloadPwdChngRequest): Promise<void> {
    const resp = await this.httpClient.post(PWD_CHNG_REQUEST, payload);

    if (resp.status !== 200) {
      throw new Error(resp.data);
    }
  }

  async pwdChngReset(payload: PayloadPwdChngReset): Promise<RespPwdChngReset | ErrPwdChngReset> {
    try {
      const resp: AxiosResponse<RespPwdChngReset> = await this.httpClient.post(
        PWD_CHNG_RESET,
        payload
      );

      return resp.data;
    } catch (err) {
      const axiosErr = err as AxiosError;
      if (axiosErr.response) {
        return (axiosErr.response.data as unknown) as ErrPwdChngReset;
      }

      throw new Error(err);
    }
  }

  async updateProfile(payload: PayloadAuthenticated, settings: Profile): Promise<Profile> {
    const resp: AxiosResponse<Profile> = await this.httpClient.post(
      PROFILE,
      { ...settings },
      { headers: buildAuthHeaders(payload) }
    );
    return resp.data;
  }

  async setPasswordWithTmpPassword(payload: any) {
    return this.httpClient.post(SET_PASSWORD_WITH_TMP_PASSWORD, payload);
  }

  async setPasswordWithToken(payload: any) {
    return this.httpClient.post(SET_PASSWORD_WITH_JWT, payload);
  }

  async tagAlias(payload: any) {
    const headers = this.buildAuthHeaders(payload.authInfo);
    return this.httpClient.post(TAG_ALIAS, {}, { headers });
  }

  async userAliases(token: any) {
    return this.httpClient.get(ALIASES, {
      headers: { Authorization: `Token ${token}` }
    });
  }

  async validateJwt({ jwt }: any) {
    return this.httpClient.post(VALIDATE_JWT, { token: jwt });
  }

  async validateResetPasswordJwt({ jwt }: any) {
    return this.httpClient.post(VALIDATE_RESET_PASSWORD_JWT, { token: jwt });
  }

  /**
   * @param token to be validated JWT
   * @returns an authToken value if the provided JWT if valid. Otherwise an
   * exception (non-200 response) is thrown
   */
  async validateSsoToken(token: string): Promise<string> {
    const resp: AxiosResponse<RespPwdChngReset> = await this.httpClient.post(
      API_ENDPOINTS.USER.VALIDATE_SSO,
      { token }
    );
    return resp.data.authToken;
  }
}

export { User };
